import agentBusinessHoursAccessAPI from 'dashboard/api/agentBusinessHoursAccess';

const safeRestrictedAccess = (accountId, reason = 'pending') => ({
  account_id: Number(accountId),
  mode: 'restricted',
  reason,
  grace_expires_at: null,
  next_opening_at: null,
  next_evaluation_at: null,
});

const unrestrictedCommunityAccess = accountId => ({
  account_id: Number(accountId),
  mode: 'full',
  reason: 'enterprise_feature_unavailable',
  grace_expires_at: null,
  next_opening_at: null,
  next_evaluation_at: null,
});

export const initialState = () => ({
  records: {},
  fetching: {},
  errors: {},
});

export const state = initialState();

const accessForAccount = ($state, accountId) =>
  $state.records[accountId] || safeRestrictedAccess(accountId);

export const getters = {
  getForAccount: $state => accountId => accessForAccount($state, accountId),
  isResolved: $state => accountId => Boolean($state.records[accountId]),
  isFetching: $state => accountId => Boolean($state.fetching[accountId]),
  getError: $state => accountId => $state.errors[accountId] || null,
  isFull: $state => accountId =>
    accessForAccount($state, accountId).mode === 'full',
  isGrace: $state => accountId =>
    accessForAccount($state, accountId).mode === 'grace',
  isRestricted: $state => accountId =>
    accessForAccount($state, accountId).mode === 'restricted',
};

export const mutations = {
  SET_AGENT_BUSINESS_HOURS_ACCESS($state, access) {
    $state.records = {
      ...$state.records,
      [access.account_id]: access,
    };
  },
  SET_AGENT_BUSINESS_HOURS_ACCESS_FETCHING($state, { accountId, value }) {
    $state.fetching = { ...$state.fetching, [accountId]: value };
  },
  SET_AGENT_BUSINESS_HOURS_ACCESS_ERROR($state, { accountId, error }) {
    $state.errors = { ...$state.errors, [accountId]: error };
  },
};

export const actions = {
  async fetch({ commit }, accountId) {
    const normalizedAccountId = Number(accountId);

    if (window.chatwootConfig?.isEnterprise !== 'true') {
      const access = unrestrictedCommunityAccess(normalizedAccountId);
      commit('SET_AGENT_BUSINESS_HOURS_ACCESS', access);
      return access;
    }

    commit('SET_AGENT_BUSINESS_HOURS_ACCESS_FETCHING', {
      accountId: normalizedAccountId,
      value: true,
    });
    commit('SET_AGENT_BUSINESS_HOURS_ACCESS_ERROR', {
      accountId: normalizedAccountId,
      error: null,
    });

    try {
      const { data } =
        await agentBusinessHoursAccessAPI.get(normalizedAccountId);
      const access = data.agent_business_hours_access;
      commit('SET_AGENT_BUSINESS_HOURS_ACCESS', access);
      return access;
    } catch (error) {
      const access =
        error.response?.data?.agent_business_hours_access ||
        safeRestrictedAccess(normalizedAccountId, 'status_unavailable');
      commit('SET_AGENT_BUSINESS_HOURS_ACCESS_ERROR', {
        accountId: normalizedAccountId,
        error,
      });
      commit('SET_AGENT_BUSINESS_HOURS_ACCESS', access);
      return access;
    } finally {
      commit('SET_AGENT_BUSINESS_HOURS_ACCESS_FETCHING', {
        accountId: normalizedAccountId,
        value: false,
      });
    }
  },
  ensure({ dispatch, getters: $getters }, accountId) {
    if ($getters.isResolved(accountId)) {
      return $getters.getForAccount(accountId);
    }
    return dispatch('fetch', accountId);
  },
  applyEnvelope({ commit }, access) {
    if (access?.account_id) {
      commit('SET_AGENT_BUSINESS_HOURS_ACCESS', access);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
